import { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { handleGaClick } from '@/utils/gaDataPush';
import { getDataAttributes } from '@/utils/dataAttributes';
import { getComponentMargin, getAnchorId } from '@/utils/builderUtils';
import { GaEventsProps } from '@/store/types/IBuilder';
import useStore from '@/store/useStore';
import RichText from '@/components/RichText/RichText';

export type HeaderBannerProps = {
  /**
   * Background color of the banner
   */
  backgroundColor: string;
  /**
   * Color of the content text
   */
  fontColor: string;
  /**
   * Turn on to add margin to the bottom of the component
   */
  margin?: boolean;
  /**
   * Give to the component an anchor link id
   */
  anchorLinkID?: string;
  /**
   * Optional list of data attributes used to send Google Analytics Events
   */
  gaEvents?: GaEventsProps[];
  content?: any;
};

export default function HeaderBanner({
  backgroundColor,
  fontColor,
  margin = false,
  gaEvents,
  anchorLinkID,
  content,
}: HeaderBannerProps) {
  const color = fontColor || 'white';
  const contentFromStore = useStore(state => state.builderData?.promo?.topPurpleBar);
  content = content || contentFromStore;
  const [plainText, setPlainText] = useState('');
  const richTextDT = useRef<HTMLElement>();
  const richTextMobile = useRef<HTMLElement>();

  const onClick = (e: any) => {
    if (content && content.link) {
      handleGaClick(e, 'internal_link_click', content?.link, plainText, 'Top Bar');
      (window as any).location = content?.link;
    }
  };

  const insertInlineAnchor = (link: string, node: HTMLElement) => {
    // Get last leaf child element
    let leafElement: Node = node;
    while (leafElement?.lastChild !== null) {
      leafElement = leafElement?.lastChild;
    }
    // Insert carrot in last child leaf element if this does not exists
    let carrot: HTMLAnchorElement;
    const carrotID = 'inline-promo-carrot';
    if (node.querySelector(`a#${carrotID}`)) {
      // It exists
      carrot = node.querySelector(`span#${carrotID}`) as HTMLAnchorElement;
    } else {
      carrot = document.createElement('span') as HTMLAnchorElement;

      carrot.id = carrotID;
      carrot.innerText = ' >';
      carrot.style.fontSize = '9px';
      carrot.style.color = 'white';
      carrot.style.textDecoration = 'none';
      carrot.style.lineHeight = '1rem';
      carrot.style.fontWeight = 'light';
      carrot.style.fontStyle = 'initial';

      leafElement.parentElement?.appendChild(carrot);
    }
  };

  useEffect(() => {
    if (content?.link && richTextDT.current) {
      insertInlineAnchor(content.link, richTextDT.current);
    }
  }, [content?.text, content?.link]);

  useEffect(() => {
    if (content?.link && richTextMobile.current) {
      insertInlineAnchor(content.link, richTextMobile.current);
    }
  }, [content?.mobileText, content?.link]);

  useEffect(() => {
    const htmlToPlainText = html => {
      const temporaryElement = document.createElement('div');
      temporaryElement.innerHTML = html;
      const convertedText = temporaryElement.textContent || temporaryElement.innerText || '';
      temporaryElement.remove();
      return convertedText;
    };

    if (typeof document !== 'undefined') {
      setPlainText(htmlToPlainText(content?.text || content?.mobileText));
    }
  }, [content]);

  const messageCss =
    'tw-inline tw-tracking-widest tw-text-be-3xs md:tw-text-be-xs [&_p]:tw-text-be-3xs [&_p]:md:tw-text-be-xs [&_p]:tw-font-base';

  const desktopText = <RichText className={messageCss} content={content?.text?.trim()} ref={richTextDT} />;

  const mobileText = <RichText className={messageCss} content={content?.mobileText?.trim()} ref={richTextMobile} />;

  return (
    <div
      {...getAnchorId(anchorLinkID)}
      style={{ color, backgroundColor }}
      {...getDataAttributes({ gaEvents })}
      onClick={onClick}
      className={classnames(
        'tw-no-underline tw-h-[40px] lg:tw-h-[32px] tw-font-book',
        'focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]',
        getComponentMargin(margin),
        content?.link && 'tw-cursor-pointer'
      )}
      role="link"
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          onClick(event);
        }
      }}
      data-ge-hide=""
    >
      <div
        className="site-top-alert tw-flex tw-justify-center tw-items-center tw-h-[40px] lg:tw-h-[32px]"
        style={{ backgroundColor }}
      >
        <div className="alert tw-tracking-widest tw-mb-0 tw-border-none tw-text-center tw-font-book !tw-px-0 !tw-py-[0px] large:!tw-py-[5px] tw-text-[10px] md:tw-text-[12px] tw-max-h-[2.66rem] tw-overflow-hidden tw-leading-[1.428571429]">
          {content && content.link ? (
            <>
              <span className="tw-hidden md:tw-inline [&_>_div]:tw-inline" style={{ color, textDecoration: 'none' }}>
                {desktopText}
              </span>

              <span className="md:tw-hidden [&_>_div]:tw-inline" style={{ color, textDecoration: 'none' }}>
                {mobileText}
              </span>
            </>
          ) : (
            <>
              <span className="tw-hidden md:tw-inline tw-cursor-default" style={{ color, textDecoration: 'none' }}>
                {desktopText}
              </span>
              <span className="md:tw-hidden tw-cursor-default" style={{ color, textDecoration: 'none' }}>
                {mobileText}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
